import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Alert, Skeleton, Space, Tabs } from "antd";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "@/shared/hooks/usePermission";
import { DepartmentEmployeesTab } from "../components/DepartmentEmployeesTab";
import { DepartmentOverviewTab } from "../components/DepartmentOverviewTab";
import { DepartmentProfileHeader } from "../components/DepartmentProfileHeader";
import { DepartmentSettingsTab } from "../components/DepartmentSettingsTab";
import { DepartmentSubunitsTab } from "../components/DepartmentSubunitsTab";
import { DepartmentTasksTab } from "../components/DepartmentTasksTab";
import { DepartmentZonesTab } from "../components/DepartmentZonesTab";
import {
	useArchiveDepartment,
	useDepartment,
	useUnarchiveDepartment,
} from "../hooks/useDepartments";
import type { DepartmentDetail } from "../types";
import {
	DEPARTMENT_TABS,
	type DepartmentTab,
	departmentTabLabel,
	isTabAllowed,
	resolveTab,
} from "../utils/tabs";

/**
 * Yorliq → tarkib.
 *
 * ⚠️ `Tabs.items` ichida chaqiriladi, lekin `destroyOnHidden` tufayli FAQAT
 * faol yorliqning elementi mount bo'ladi — olti yorliqning so'rovlari bir
 * vaqtda ketmaydi (`EmployeeDetailPage` naqshi).
 *
 * ⚠️ «Sozlamalar» TO'LIQ obyektni oladi — tahrirlash/ko'chirish modallari
 * `DepartmentDetail` ni kutadi va u allaqachon keshda, ikkinchi so'rov
 * keraksiz.
 */
function renderTab(tab: DepartmentTab, department: DepartmentDetail, onDeleted: () => void) {
	switch (tab) {
		case "umumiy":
			return <DepartmentOverviewTab departmentId={department.id} />;
		case "bolinmalar":
			return <DepartmentSubunitsTab department={department} />;
		case "xodimlar":
			return <DepartmentEmployeesTab departmentId={department.id} isActive={department.isActive} />;
		case "hududlar":
			return <DepartmentZonesTab departmentId={department.id} isActive={department.isActive} />;
		case "topshiriqlar":
			return <DepartmentTasksTab departmentId={department.id} isActive={department.isActive} />;
		case "sozlamalar":
			return <DepartmentSettingsTab department={department} onDeleted={onDeleted} />;
	}
}

/**
 * Bo'lim profili — `/departments/$id` (spec §8).
 *
 * ⚠️ `useParams({ strict: false })` — marshrut obyekti `routeTree.tsx` da
 * va uni shu yerdan import qilish poydevorga qayta bog'lanish yaratardi
 * (`EmployeeDetailPage` naqshi).
 *
 * ⚠️ Sarlavhadagi «Tahrirlash» / «Ko'chirish» / «Yangi bo'linma» modal
 * OCHMAYDI — tegishli yorliqqa (`sozlamalar` / `bolinmalar`) o'tkazadi,
 * haqiqiy tugmalar o'sha yerda. ATAYLAB soddalashtirish: modal holatini ikki
 * joyda (sarlavha va yorliq) boshqarish murakkablikni oshirardi. Kelajakda
 * sarlavhadan to'g'ridan-to'g'ri modal kerak bo'lsa, holat shu sahifa
 * darajasiga ko'tarilib yorliqlarga prop sifatida uzatiladi.
 */
export function DepartmentDetailPage() {
	const { t } = useTranslation("departments");
	const params = useParams({ strict: false }) as { id?: string };
	const search = useSearch({ strict: false }) as { tab?: string };
	const navigate = useNavigate();
	const { can } = usePermission();
	const archiveDepartment = useArchiveDepartment();
	const unarchiveDepartment = useUnarchiveDepartment();

	// ⚠️ Yorliqlar ruxsatga qarab FILTRLANADI, `disabled` qilinmaydi:
	// ko'rinadigan, lekin har bosishda 403 beradigan yorliq foydalanuvchiga
	// tizim buzuq degan taassurot berardi.
	const yorliqlar = useMemo(() => DEPARTMENT_TABS.filter((tab) => isTabAllowed(tab, can)), [can]);

	// ⚠️ `resolveTab` RUXSAT ETILGANLAR ro'yxatini oladi — usiz `?tab=hududlar`
	// bilan kelgan `zones:read` siz foydalanuvchi mavjud bo'lmagan yorliqni
	// faol deb ko'rib, `Tabs` ni BO'SH qoldirardi.
	const activeTab = resolveTab(search.tab, yorliqlar);

	// ⚠️ `replace: true` — yorliqni har almashtirish tarixga yozilsa, «orqaga»
	// tugmasi foydalanuvchini profildan chiqarish o'rniga yorliqlar bo'ylab
	// orqaga surardi. Sarlavha tugmalari ham AYNI shu yo'ldan o'tadi — ular
	// ham faqat yorliq almashtiradi.
	const handleTabChange = useCallback(
		(key: string) => {
			navigate({
				to: ".",
				search: (eski: Record<string, unknown>) => ({ ...eski, tab: key }),
				replace: true,
			});
		},
		[navigate]
	);

	const { data: department, isLoading, isError } = useDepartment(params.id);

	if (isLoading) {
		return <Skeleton active />;
	}

	if (isError || !department) {
		return <Alert type="error" showIcon title={t("detail.loadError")} />;
	}

	// O'chirilgan bo'lim profilida qolib bo'lmaydi — ota bo'limga, u doirada
	// ko'rinmasa (`ancestors` doira bilan kesilgan, spec §6.2) ro'yxatga.
	const ota = department.ancestors[department.ancestors.length - 1];
	const handleDeleted = () => {
		if (ota && ota.id === department.parentId) {
			navigate({ to: "/departments/$id", params: { id: ota.id } });
		} else {
			navigate({ to: "/departments" });
		}
	};

	return (
		<Space orientation="vertical" size="middle" className="w-full">
			<DepartmentProfileHeader
				department={department}
				onEdit={() => handleTabChange("sozlamalar")}
				onAddChild={() => handleTabChange("bolinmalar")}
				onMove={() => handleTabChange("sozlamalar")}
				onArchive={() => archiveDepartment.mutate(department.id)}
				onUnarchive={() => unarchiveDepartment.mutate(department.id)}
			/>

			{/* ⚠️ `destroyOnHidden` — usiz ko'rilgan yorliq mount'da qolib, uning
			    so'rovlari fokus qaytganda BIRGA refetch qilardi. */}
			<Tabs
				activeKey={activeTab}
				onChange={handleTabChange}
				destroyOnHidden
				items={yorliqlar.map((tab) => ({
					key: tab,
					label: departmentTabLabel(t, tab),
					children: renderTab(tab, department, handleDeleted),
				}))}
			/>
		</Space>
	);
}
